import React, { createContext, useContext, useEffect, useState } from 'react';
import axiosInstance from '../api/axios';
import { isPublicPath } from '../routes';

interface ThemeSettings {
  primaryColor?: string | null;
  logoUrl?: string | null;
}

interface ThemeContextValue extends ThemeSettings {
  setTheme: (settings: ThemeSettings) => void;
  resetTheme: () => void;
}

const DEFAULT_PRIMARY_COLOR = '#1e40af'; // Tailwind blue-800 fallback

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// Simple in-memory guard so we only fetch organization settings once per
// frontend session, even under React StrictMode double-invocation in dev.
let orgSettingsFetched = false;
const AUTH_BOOTSTRAP_EVENT = "skeptics-auth-bootstrap-ready";
const AUTH_BOOTSTRAP_FLAG = "__SKEPTICS_AUTH_BOOTSTRAP_IN_PROGRESS__";

function applyThemeToDocument() {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.setProperty('--primary-color', DEFAULT_PRIMARY_COLOR);
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [primaryColor, setPrimaryColor] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  // Initialize theme; prefer fetching live org settings when we know the tenant/org
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const loadTheme = () => {
      const storedColor = window.localStorage.getItem('orgThemeColor');
      const storedLogo = window.localStorage.getItem('orgLogoUrl');
      const path = window.location.pathname.replace(/\/$/, '') || '/';
      const isPublicRoute = isPublicPath(path);
      const storedSlug =
        window.localStorage.getItem('organization_slug') || '';

      if (isPublicRoute) {
        // Public routes always use default branding.
        applyThemeToDocument();
        setPrimaryColor(null);
        setLogoUrl(null);
        return;
      }

      // If we have an org/tenant slug, fetch fresh settings so theme updates per-tenant/admin.
      // Guard with orgSettingsFetched so we don't hit the backend multiple times.
      if (storedSlug) {
        if (orgSettingsFetched) {
          applyThemeToDocument();
          // setPrimaryColor(storedColor); // temp undone
          setPrimaryColor(null);
          setLogoUrl(storedLogo);
          return;
        }
        orgSettingsFetched = true;
        axiosInstance
          .get(`/api/organization-settings/${storedSlug}`)
          .then((res) => {
            const raw = res.data || {};
            const settings = (raw as any).settings || raw;
            let themeColor = (settings.theme_color as string | undefined) ?? storedColor ?? null;
            const logoFromApi = (settings.logo_url as string | undefined) ?? storedLogo ?? null;

            if (typeof themeColor === 'string') {
              themeColor = themeColor.trim();
              if (themeColor.startsWith('^')) {
                themeColor = themeColor.slice(1);
              }
            }

            const nextColor = themeColor;
            const nextLogo = logoFromApi;

            applyThemeToDocument();
            // setPrimaryColor(nextColor ?? null); // temp undo
            setPrimaryColor(null);
            setLogoUrl(nextLogo ?? null);

            if (nextColor) {
              window.localStorage.setItem('orgThemeColor', nextColor);
            }
            if (nextLogo) {
              window.localStorage.setItem('orgLogoUrl', nextLogo);
            }
          })
          .catch(() => {
            // On failure, fall back to whatever we had stored locally
            applyThemeToDocument();
            // setPrimaryColor(storedColor); //temp undone
            setPrimaryColor(null);
            setLogoUrl(storedLogo);
          });
        return;
      }

      // No slug: fall back to last-known theme for this origin
      applyThemeToDocument();
      // setPrimaryColor(storedColor); //temp undone
      setPrimaryColor(null);
      setLogoUrl(storedLogo);
    };

    if ((window as any)[AUTH_BOOTSTRAP_FLAG]) {
      const onReady = () => {
        window.removeEventListener(AUTH_BOOTSTRAP_EVENT, onReady);
        loadTheme();
      };
      window.addEventListener(AUTH_BOOTSTRAP_EVENT, onReady);
      return () => {
        window.removeEventListener(AUTH_BOOTSTRAP_EVENT, onReady);
      };
    }

    loadTheme();
  }, []);

  const setTheme = (settings: ThemeSettings) => {
    const nextColor = settings.primaryColor ?? null;
    const nextLogo = settings.logoUrl ?? null;

    // setPrimaryColor(nextColor); //temp undone
    setPrimaryColor(null);
    setLogoUrl(nextLogo);

    if (typeof window !== 'undefined') {
      if (nextColor) {
        window.localStorage.setItem('orgThemeColor', nextColor);
      } else {
        window.localStorage.removeItem('orgThemeColor');
      }

      if (nextLogo) {
        window.localStorage.setItem('orgLogoUrl', nextLogo);
      } else {
        window.localStorage.removeItem('orgLogoUrl');
      }
    }

    applyThemeToDocument();
  };

  const resetTheme = () => {
    setTheme({ primaryColor: null, logoUrl: null });
  };

  return (
    <ThemeContext.Provider value={{ primaryColor, logoUrl, setTheme, resetTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
};

